/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import styled from 'styled-components';
import { capitalize } from '@veeam-vspc/core';
import { Dialog, FieldLayout, SPACING_XL, FormLayout, Text, DIALOG_SIZE } from '@veeam-vspc/components';

import { useLang } from 'views/providers/LangProvider/hooks';
import { normalizeDateWithTime } from 'core/utils/date-helpers';
import { useAppStore } from 'views/providers/AppProvider/hooks';
import { useEmailServerSettingsStore } from '../../../../../../hooks';

import type { CertificateInfoDialogProps } from './interfaces';

const CustomFieldLayout = styled(FieldLayout)`
    .vm-FieldLabel {
        label {
            max-height: 18px;
        }
    }

    .vm-FormFieldControl {
        vertical-align: middle;
        padding-left: ${SPACING_XL};
    }
`;

const TextWrap = styled(Text)`
    white-space: normal;
`;

const DialogWrap = styled(Dialog)`
    max-width: 720px;
`;

export const CertificateInfoDialog: React.FC<CertificateInfoDialogProps> = (props) => {
    const lang = useLang();
    const { formats } = useAppStore();
    const { deactivateDialog } = props;
    const serverSettingsStore = useEmailServerSettingsStore();
    const certInfoData = serverSettingsStore.emailServerTestSmtpSettings.serverCertificate;
    const { subjectName, issuerName, hash, serialNumber, notBefore, notAfter, signatureAlgorithm } = certInfoData;

    return (
        <DialogWrap
            onRequestClose={deactivateDialog}
            header={capitalize(lang.SECURITY_CERTIFICATE)}
            size={{
                width: DIALOG_SIZE.auto,
                height: DIALOG_SIZE.auto,
            }}
            actions={[
                {
                    text: lang.CLOSE,
                    onClick: deactivateDialog,
                },
            ]}
        >
            <FormLayout inlineLabel>
                <CustomFieldLayout label={lang.SUBJECT}>
                    <TextWrap>{subjectName}</TextWrap>
                </CustomFieldLayout>

                <CustomFieldLayout label={lang.ISSUER}>
                    <TextWrap>{issuerName}</TextWrap>
                </CustomFieldLayout>

                <CustomFieldLayout label={lang.THUMBPRINT}>
                    <TextWrap>{hash}</TextWrap>
                </CustomFieldLayout>

                <CustomFieldLayout label={lang.SERIAL_NUMBER}>
                    <TextWrap>{serialNumber}</TextWrap>
                </CustomFieldLayout>

                <CustomFieldLayout label={lang.VALID_FROM}>
                    <TextWrap>{normalizeDateWithTime(new Date(Date.parse(notBefore)), formats) || lang.DASH_SYMBOL}</TextWrap>
                </CustomFieldLayout>

                <CustomFieldLayout label={lang.VALID_TO}>
                    <TextWrap>{normalizeDateWithTime(new Date(Date.parse(notAfter)), formats) || lang.DASH_SYMBOL}</TextWrap>
                </CustomFieldLayout>

                <CustomFieldLayout label={lang.SIGNATURE_ALGORITHM}>
                    <TextWrap>{signatureAlgorithm}</TextWrap>
                </CustomFieldLayout>
            </FormLayout>
        </DialogWrap>
    );
};
